// Turn the auto-design service's engineering summary into a user-facing cause.
// Keep model names untouched; the server's diagnostic JSON remains available to admins.
const PHASE_SUMMARY = /^(.+?) is (\d+)-phase, and a plant this size is designed as (\d+)-phase\.?$/i;
const FILTER_PHASE_SUMMARY = /^(.+?) is (\d+)-phase, while the inverter phase filter allows (\d+)-phase\.?$/i;
const PINNED_PHASE = /^Inverter's (.+?) phases do not match the required phases: (\d+)\.?$/i;

// "13,500" from the engine is "13.500" in Greek.
const greekArea = (text) => String(text).replace(/,/g, "\u0000").replace(/\./g, ",").replace(/\u0000/g, ".");

function greekEngineeringCause(summary, searchingCatalog = false) {
  let match = summary.match(/^(.+?) was rejected because its nominal AC voltage/i);
  if (match) return {
    cause: searchingCatalog
      ? `Ο σχεδιαστής δοκίμασε αντιστροφείς από τον κατάλογο. Ένας υποψήφιος που απορρίφθηκε λόγω ασύμβατης ονομαστικής τάσης AC ήταν ο ${match[1]}. Δεν σημαίνει ότι τον επιλέξατε εσείς ή ότι ήταν η μοναδική αιτία αποτυχίας.`
      : `Ο αντιστροφέας ${match[1]} δεν έχει συμβατή ονομαστική τάση AC για το έργο.`,
    action: searchingCatalog
      ? "Ελέγξτε την τάση σύνδεσης και τους συνολικούς λόγους απόρριψης στα διαγνωστικά της αναζήτησης."
      : "Ελέγξτε την τάση σύνδεσης και τα στοιχεία του αντιστροφέα στον κατάλογο ή επιλέξτε άλλο μοντέλο.",
  };
  match = summary.match(/^(.+?) is too small: its rated AC power/i);
  if (match) return {
    cause: `Η ονομαστική ισχύς AC του ${match[1]} είναι πολύ μικρή για τον στόχο της μελέτης.`,
    action: "Επιλέξτε ισχυρότερο αντιστροφέα ή μειώστε τον στόχο ισχύος DC.",
  };
  match = summary.match(/^(.+?): no string length satisfies/i);
  if (match) return {
    cause: `Με τον ${match[1]} δεν προκύπτει μήκος string που να ικανοποιεί τα ηλεκτρικά όρια πλαισίου και MPPT.`,
    action: "Ελέγξτε τον συνδυασμό πλαισίου και αντιστροφέα ή αφήστε τον σχεδιαστή να επιλέξει εξοπλισμό.",
  };
  match = summary.match(/^(.+?): the strings needed do not fit/i);
  if (match) return {
    cause: `Τα απαιτούμενα strings δεν χωρούν στις εισόδους MPPT του ${match[1]}.`,
    action: "Επιλέξτε αντιστροφέα με περισσότερες κατάλληλες εισόδους MPPT ή αλλάξτε τον στόχο ισχύος.",
  };
  if (/do not fit inside the drawn parcel/i.test(summary)) return {
    cause: "Η απαιτούμενη διάταξη πλαισίων δεν χωρά στο σχεδιασμένο γήπεδο με τις τρέχουσες αποστάσεις.",
    action: "Ελέγξτε το περίγραμμα, το περιθώριο ασφαλείας, το πλήθος πλαισίων και το εύρος στοίβαξης.",
  };
  if (/DC\/AC (?:window|ratio)|inside the DC\/AC window/i.test(summary)) return {
    cause: "Κανένας διαθέσιμος συνδυασμός δεν ικανοποιεί τα όρια λόγου DC/AC.",
    action: "Διευρύνετε τα όρια DC/AC, αλλάξτε τον στόχο ισχύος ή επιλέξτε άλλο μέγεθος αντιστροφέα.",
  };
  match = summary.match(/^(.+?) modules cover ([\d,.]+) m2 on a flush roof \(no row spacing\), and the roof has ([\d,.]+) m2/i);
  if (match) return {
    cause: `Τα πλαίσια ${match[1]} καλύπτουν ${greekArea(match[2])} m² στη στέγη (συνεπίπεδη τοποθέτηση, χωρίς αποστάσεις σειρών), ενώ η διαθέσιμη επιφάνεια είναι ${greekArea(match[3])} m².`,
    action: "Ελέγξτε την επιφάνεια της στέγης ή μειώστε τον στόχο ισχύος.",
  };
  match = summary.match(/^(.+?) needs ([\d,.]+) m2 of ground including row spacing, and the site has ([\d,.]+) m2/i);
  if (match) return {
    cause: `Με το πλαίσιο ${match[1]} απαιτούνται ${greekArea(match[2])} m² μαζί με τις αποστάσεις μεταξύ σειρών, ενώ η διαθέσιμη επιφάνεια είναι ${greekArea(match[3])} m².`,
    action: "Ελέγξτε τη διαθέσιμη έκταση, την κλίση και τον προσανατολισμό εδάφους και πλαισίων ή τον στόχο ισχύος.",
  };
  if (/needs .* of ground including row spacing/i.test(summary)) return {
    cause: "Η απαιτούμενη επιφάνεια, μαζί με τις αποστάσεις μεταξύ σειρών, υπερβαίνει τη διαθέσιμη.",
    action: "Ελέγξτε τη διαθέσιμη έκταση, τις αποστάσεις σειρών ή τον στόχο ισχύος.",
  };
  return null;
}

export function describeAutoDesignFailure(error, greek = false) {
  const message = String(error?.message || "").trim();
  const summary = String(error?.summary || "").trim();
  const candidateFilterFailure = [
    [/^No PV modules belong to your organisation for auto-design\.?$/i,
      "Δεν υπάρχουν Φ/Β πλαίσια του οργανισμού σας διαθέσιμα για αυτόματο σχεδιασμό.",
      "Προσθέστε πλαίσιο στον εξοπλισμό του οργανισμού ή απενεργοποιήστε τον περιορισμό «Μόνο πλαίσια του οργανισμού μου»."],
    [/^No inverters belong to your organisation for auto-design\.?$/i,
      "Δεν υπάρχουν αντιστροφείς του οργανισμού σας διαθέσιμοι για αυτόματο σχεδιασμό.",
      "Προσθέστε αντιστροφέα στον εξοπλισμό του οργανισμού ή απενεργοποιήστε τον περιορισμό «Μόνο αντιστροφείς του οργανισμού μου»."],
    [/^The selected PV module does not belong to your organisation\.?$/i,
      "Το επιλεγμένο Φ/Β πλαίσιο δεν ανήκει στον οργανισμό σας.",
      "Επιλέξτε δικό σας πλαίσιο ή απενεργοποιήστε τον περιορισμό ιδιοκτησίας."],
    [/^The selected inverter does not belong to your organisation\.?$/i,
      "Ο επιλεγμένος αντιστροφέας δεν ανήκει στον οργανισμό σας.",
      "Επιλέξτε δικό σας αντιστροφέα ή απενεργοποιήστε τον περιορισμό ιδιοκτησίας."],
    [/^No visible PV modules match the selected auto-design technologies\.?$/i,
      "Δεν υπάρχουν Φ/Β πλαίσια που να ικανοποιούν τα φίλτρα αναζήτησης.",
      "Ελέγξτε την τεχνολογία πλαισίου και τον περιορισμό εξοπλισμού του οργανισμού σας."],
    [/^No visible inverters match the selected auto-design conversion model\.?$/i,
      "Δεν υπάρχουν αντιστροφείς που να ικανοποιούν τα φίλτρα αναζήτησης.",
      "Ελέγξτε το μοντέλο απόδοσης αντιστροφέα και τον περιορισμό εξοπλισμού του οργανισμού σας."],
  ].find(([pattern]) => pattern.test(message));
  if (candidateFilterFailure) return greek ? {
    title: "Δεν βρέθηκε εξοπλισμός για αυτόματο σχεδιασμό.",
    cause: candidateFilterFailure[1],
    action: candidateFilterFailure[2],
  } : {
    title: "No equipment was available for auto-design.",
    cause: message,
    action: "Review the available equipment and candidate filters.",
  };
  let diagnostics = error?.diagnostics;
  if (typeof diagnostics === "string") {
    try { diagnostics = JSON.parse(diagnostics); } catch { diagnostics = null; }
  }
  const limitHit = error?.limitHit || error?.limit_hit || diagnostics?.limit_hit;
  if (limitHit) {
    const groups = Number(error?.candidateGroups ?? diagnostics?.candidate_groups);
    const maxGroups = Number(error?.maxCandidateGroups ?? diagnostics?.search_limits?.max_candidate_groups);
    const groupLimit = limitHit === "candidate_groups";
    const reached = groupLimit && Number.isFinite(groups) && groups > 0
      ? (Number.isFinite(maxGroups) && maxGroups > 0 ? `${groups}/${maxGroups}` : String(groups))
      : null;
    return greek ? {
      title: "Η αναζήτηση αυτόματου σχεδιασμού δεν ολοκληρώθηκε.",
      cause: groupLimit
        ? `Σταμάτησε στο όριο υποψήφιων συνδυασμών${reached ? ` (${reached})` : ""}, πριν εξετάσει όλο τον διαθέσιμο εξοπλισμό. Αυτό δεν αποδεικνύει ότι δεν υπάρχει συμβατή λύση.`
        : "Σταμάτησε σε όριο χρόνου ή υπολογισμών πριν ολοκληρωθεί. Αυτό δεν αποδεικνύει ότι δεν υπάρχει συμβατή λύση.",
      action: "Δοκιμάστε με συγκεκριμένο πλαίσιο ή αντιστροφέα, ή περιορίστε τα φίλτρα αναζήτησης.",
    } : {
      title: "The auto-design search did not finish.",
      cause: groupLimit
        ? `It reached the candidate-group limit${reached ? ` (${reached})` : ""} before checking all available equipment. This does not prove that no compatible design exists.`
        : "It reached a time or computation limit before finishing. This does not prove that no compatible design exists.",
      action: "Try a selected module or inverter, or narrow the search filters.",
    };
  }
  const noStringAllocation = summary.match(/^No feasible string allocation after (\d+) inverter candidate groups\./i);
  if (noStringAllocation) {
    const attempts = Number(noStringAllocation[1]);
    const moduleContext = error?.requestContext?.selected_module
      ? "Με το επιλεγμένο πλαίσιο "
      : "Στην αναζήτηση ";
    return greek ? {
      title: "Δεν βρέθηκε εφικτή ηλεκτρική διάταξη.",
      cause: `${moduleContext}εξετάστηκαν ${attempts} συνδυασμοί αντιστροφέων, αλλά κανένας δεν έφτασε σε εφικτή κατανομή strings. Οι απορρίψεις τάσης AC αφορούν και πρώιμα φίλτρα του καταλόγου· ένα μεμονωμένο μοντέλο δεν εξηγεί όλη την αποτυχία.`,
      action: "Ελέγξτε τα όρια ρεύματος των εισόδων MPPT, το επιτρεπτό μήκος string και τον λόγο DC/AC για συμβατό αντιστροφέα. Δοκιμάστε άλλο μοντέλο ή διευρύνετε μόνο τα φίλτρα επιλογής εξοπλισμού.",
    } : {
      title: "No feasible electrical layout was found.",
      cause: `The selected module was checked against ${attempts} inverter groupings, but none reached a feasible string allocation. AC-voltage rejections also include early catalogue filtering; one rejected model does not explain the whole result.`,
      action: "Check MPPT input-current limits, feasible string lengths and DC/AC ratio for a compatible inverter. Try another model or broaden equipment filters only.",
    };
  }
  const searchingCatalog = Boolean(error?.requestContext) && !error.requestContext.selected_inverter;
  const phaseMatch = summary.match(PHASE_SUMMARY) || summary.match(FILTER_PHASE_SUMMARY);
  const pinnedMatch = (summary || message).match(PINNED_PHASE);
  const phase = phaseMatch || pinnedMatch;

  if (phase) {
    const inverter = phase[1];
    const actual = phaseMatch ? Number(phase[2]) : null;
    const required = Number(phaseMatch ? phase[3] : phase[2]);
    const actualLabel = actual === 1 ? "μονοφασικός" : actual === 3 ? "τριφασικός" : `${actual} φάσεων`;
    const requiredLabel = required === 1 ? "μονοφασικό" : required === 3 ? "τριφασικό" : `${required} φάσεων`;
    return greek
      ? {
          title: "Δεν βρέθηκε συμβατός ηλεκτρολογικός σχεδιασμός.",
          cause: searchingCatalog
            ? `Ο ${inverter} ήταν ένας υποψήφιος του καταλόγου που απορρίφθηκε λόγω ασύμβατων φάσεων. Δεν σημαίνει ότι τον επιλέξατε εσείς ή ότι ήταν η μοναδική αιτία αποτυχίας.`
            : actual
            ? `Ο αντιστροφέας ${inverter} είναι ${actualLabel}, ενώ το φίλτρο αναζήτησης επιτρέπει ${requiredLabel} αντιστροφέα.`
            : `Ο αντιστροφέας ${inverter} δεν έχει τον απαιτούμενο αριθμό φάσεων (${required}).`,
          action: "Αλλάξτε το φίλτρο φάσεων ή επιλέξτε συμβατό αντιστροφέα. Επιβεβαιώστε χωριστά τη διαμόρφωση της παροχής.",
        }
      : {
          title: "No compatible electrical design was found.",
          cause: searchingCatalog
            ? `${inverter} was one catalogue candidate rejected for its phase count; it was not selected by you or necessarily the only reason the search failed.`
            : phaseMatch ? summary : `Inverter ${inverter} does not have the required ${required} phases.`,
          action: "Change the phase filter or choose a compatible inverter. Verify the grid connection separately.",
        };
  }

  const noDesign = /No proposed design of the system was found/i.test(message);
  if (noDesign) {
    const localized = greek && summary ? greekEngineeringCause(summary, searchingCatalog) : null;
    const catalogueVac = searchingCatalog && !greek && summary.match(/^(.+?) was rejected because its nominal AC voltage/i);
    return {
      title: greek ? "Δεν βρέθηκε συμβατός σχεδιασμός Φ/Β." : "No compatible PV design was found.",
      cause: localized?.cause || (catalogueVac
        ? `${catalogueVac[1]} was one catalogue candidate rejected for its nominal AC voltage; it was not selected by you or necessarily the only reason the search failed.`
        : summary) || (greek
        ? "Κανένας συνδυασμός εξοπλισμού δεν ικανοποίησε όλες τις παραδοχές."
        : "No equipment combination met every constraint."),
      action: localized?.action || (greek
        ? "Ελέγξτε τον στόχο ισχύος, τον επιλεγμένο εξοπλισμό, τα όρια DC/AC και τους περιορισμούς διάταξης."
        : "Check the target size, selected equipment, DC/AC limits, and layout constraints."),
    };
  }

  return {
    title: greek ? "Ο αυτόματος σχεδιασμός δεν ολοκληρώθηκε." : "Auto-design could not finish.",
    cause: summary || message || (greek ? "Δεν δόθηκαν λεπτομέρειες από τον διακομιστή." : "The server returned no details."),
    action: null,
  };
}
